import React, { useEffect, useState, useRef } from 'react';
import { Image } from 'antd';
import { useHistory, useLocation } from 'react-router-dom';
import { Text, BoldText, SpaceEnd } from '../../../../styles/common.styles';

import {
    DivisionsContainer,
    EC3Link,
    NoProductsTitleContainer,
    NoProductsTitleContainerFiltered,
    ProductSpin,
    ProductsTabContent,
    ProductsTabHeader,
    ProductsTabPageContainer,
    OpenCloseAllButton,
    ViewProjectInEC3Button,
    FoundResultsCount,
    ProductFilterContainer,
    SelectAllContainer,
    SelectAllCheckbox
} from './ProductsTab.styles';
import ProductsSearch from '../../../../components/products/productsTable/ProductsSearch';
import { GetProjectInformationResponse } from '../../../../redux/actions/projectActions/types';
import { black, blueLink } from '../../../../styles/colors';
import ProductsAction from '../../../../components/common/productsAction/ProductsAction';
import { useAppDispatch, useAppSelector } from '../../../../hooks/redux';
import {
    getProductReferencesAction,
    getProductIdsByPackageIdAction,
    addProductsToProjectAction,
    getProjectProductsWithCopyTokensOptionAction,
} from '../../../../redux/actions/productActions';
import { ActionResponse } from '../../../../models/actionResponse/types';
import {
    AddProductsToProjectResponse,
    GetProductIdsByPackageIdResponse,
    ProductReferences,
} from '../../../../redux/actions/productActions/types';
import DivisionItem from './divisionItem/DivisionItem';
import { Division, Section } from '../../../../models/productDivision/types';
import {
    BrandCompany,
    ExternalProduct,
    InternalProduct,
    Product,
} from '../../../../models/product/types';
import { usePermissions } from '../../../../hooks/permissions';
import { ProjectPermissionTypes } from '../../../../models/permission/enums';
import { ProjectReferences } from '../../../../models/project/types';
import { GetProductsFromTransparencyCatalogAPIResponse, ProductsFilterData } from '../../../../redux/actions/transparencyCatalogActions/types';
import ExistingProductsModal from '../../../../components/existingProductsModal/ExistingProductsModal';
import { EC3ExportStatus } from '../../../../models/ec3/enums';
import {
    getCompaniesOfProducts,
    getExternalProductsAction,
} from '../../../../redux/actions/transparencyCatalogActions';
import ProductsFilterModal, {
    ProductFilters,
} from '../../../../components/products/productsFilterModal/ProductsFilterModal';
import ProductsFilterButton from '../../../../components/products/productsFilterModal/ProductsFilterButton';
import { CheckboxChangeEvent } from 'antd/lib/checkbox';
import { getUserByIdAction } from '../../../../redux/actions/userActions';
import { UserByIdResponse } from '../../../../redux/actions/userActions/types';
import { setStoredUser } from '../../../../redux/reducers/authReducer/AuthSlice';
import { MaterialsFiltersIds } from '../../../../types/materialsFilters.enum';
import { ProjectProductTagsProvider } from './projectProductTagsContext/ProjectProductTagsContext';
import {
    getProjectProductTagsByProjectProductIdsAction,
} from '../../../../redux/actions/projectProductTagActions';
import { ProjectProductTag } from '../../../../redux/reducers/projectProductTagReducer/ProjectProductTagSlice';
import { TAGS_BATCH_SIZE } from './ProductsTab.consts';
import { mergeInternalProductsWithExternal } from '../../../../helpers/mergeProjectProducts';
import { flattenTcMfSectionProducts } from '../../../../helpers/tcExternalProducts';
import { normalizeRating, RATING_SYSTEM_TITLE_MAP } from '../../../../constants/ratingSystems';
import {
    matchesMasterFormatSearch,
    MASTERFORMAT_SEARCH_PLACEHOLDER,
} from '../../../../helpers/masterFormatSearch';

interface Props {
    projectInfo: GetProjectInformationResponse | null;
    projectReferences: ProjectReferences | null;
    allDivisions: Array<Division<Product>>;
    installedDivisions: Array<Division<Product>>;
    isFilterMode?: boolean;
    isFilteringInProgress?: boolean;
    filteredDivisions: Array<Division<Product>>;
    installedTab?: boolean;
    packageId?: string;
    setDivisions: (divisions: Array<Division<Product>>) => void;
    setAllDivisions: (divisions: Array<Division<Product>>) => Array<Division<Product>>;
    setInstalledDivisions: (divisions: Array<Division<Product>>) => Array<Division<Product>>;
    setFilteredDivisions: (divisions: Array<Division<Product>>) => Array<Division<Product>>;
    setFilteredMode: (filterMode: boolean) => boolean;
    setFilteringInProgress: (filterInProgress: boolean) => boolean;
    searchText: string;
    setSearch: (searchText: string) => string;
    productIdsInfo: Array<InternalProduct>;
    setProductIdsInfo: (productIdsI: Array<InternalProduct>) => void;
    productsExternalInfo: Array<ExternalProduct>;
    setProductsExternalInfo: (productsExternalI: Array<ExternalProduct>) => void;
    companiesExternalInfo: Array<BrandCompany>;
    setCompaniesExternalInfo: (companiesExternalI: Array<BrandCompany>) => void;
    copyProductsTo: string;
}

interface State {
    products: Array<Product>;
    pending: boolean;
    searchString: string;
}

const ProductsTab: React.FC<Props> = ({
    projectInfo,
    projectReferences,
    installedTab,
    allDivisions,
    installedDivisions,
    isFilterMode,
    isFilteringInProgress,
    filteredDivisions,
    packageId,
    setDivisions,
    setAllDivisions,
    setInstalledDivisions,
    setFilteredDivisions,
    setFilteredMode,
    setFilteringInProgress,
    searchText,
    setSearch,
    setProductIdsInfo,
    setProductsExternalInfo,
    setCompaniesExternalInfo,
    copyProductsTo
}) => {
    const existingProductIds = useRef(Array<number>());
    // Ignore stale async responses when add/copy triggers overlapping refetches (bug 892).
    const productsLoadRequestId = useRef(0);
    // Guards getData's own pending flag from being cleared by a stale/overlapping call
    // (e.g. the [projectInfo] mount effect and the projectProductsChangedAt effect firing together).
    const getDataRequestId = useRef(0);
    const lastHandledProductsChange = useRef(0);
    const dispatch = useAppDispatch();
    const selector = useAppSelector((combinedState) => combinedState);
    const { ec3Reducer, authReducer, projectReducer } = selector;
    const history = useHistory();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const [state, setState] = useState<State>({
        products: Array<Product>(),
        pending: true,
        searchString: "",
    });

    const [existingProducts, setExistingProducts] = useState({
        products: Array<{ id: number; name: string }>(),
        productIds: Array<number>(),
        isModalOpen: false,
    });

    const [filters, setFilters] = useState<ProductFilters>({
        ratingSystems: [],
        epdFilters: [],
        smEpdsFilters: [],
        ec3Filters: [],
        regulationsFilters: [],
        materialsFilters: [],
        lowEmittingFilters: [],
        lemRegulationsFilters: [],
        tags: [],
    });

    const [references, setReferences] = useState<ProductReferences | null>(null);

    const [brandsInformation, setBrandsInformation] = useState(Array<BrandCompany>());
    const [productsExternal, setProductsExternal] = useState(Array<ExternalProduct>());

    const [filterModalOpen, setFilterModalOpen] = useState(false);

    const [selectAll, setSelectAll] = useState(false);

    const [selectedProducts, setSelectedProducts] = useState(Array<Product>());

    const [openedSectionsIds, setOpenedSectionsIds] = useState(Array<string>());

    const { getProjectPermission } = usePermissions();

    useEffect(() => {
        if (projectInfo) {
            window.scroll(0, 0);
            if (!state.searchString) {
                getData();
            }
        }
        // Depend only on the project id, not the whole projectInfo object: ProjectInfo
        // recreates that object (new reference, same id) every time it refreshes tab-label
        // counts via its own projectProductsChangedAt effect. Reacting to identity here caused
        // this effect and the projectProductsChangedAt effect below to both call getData() for
        // the same product change, doubling every fetch (products, TC data, tags, companies).
    }, [projectInfo?.id]);

    // Refetch after add/copy/delete/import — clear TC caches so new rows are not hidden (bug 892).
    useEffect(() => {
        if (!projectInfo) {
            return;
        }
        const changedAt = projectReducer.projectProductsChangedAt;
        if (changedAt > 0 && changedAt !== lastHandledProductsChange.current) {
            lastHandledProductsChange.current = changedAt;
            setProductsExternal([]);
            setBrandsInformation([]);
            setState((prev) => ({ ...prev, pending: true }));
            void getData(filters, state.searchString ?? undefined);
        }
    }, [projectReducer.projectProductsChangedAt, projectInfo?.id]);

    useEffect(() => {
        console.log("939: Check Package Id and try to add new products to project");
        getProductIdsByPackageId();
    }, [projectInfo?.id, packageId]);

    const getData = async (productFilters?: ProductFilters, searchString?: string) => {
        const requestId = ++getDataRequestId.current;
        try {
            if (authReducer.user?.id) {
                const { payload } = await dispatch(
                    getUserByIdAction(authReducer.user?.id.toString())
                ) as ActionResponse<Array<UserByIdResponse>>;

                if (payload.data && payload.data.length > 0) {
                    dispatch(setStoredUser(payload.data[0]));
                }
            }
            await copyTokensFromCookiesIfAuthFailsAndGetProjectProducts(productFilters, searchString);
        } catch (e) {
            console.error('getData failed:', e);
        } finally {
            // Only the latest getData call is allowed to clear pending/filtering flags,
            // otherwise a stale overlapping call finishing first hides the spinner
            // before the real data (from the newer call) has been applied.
            if (requestId === getDataRequestId.current) {
                setState((s) => ({ ...s, pending: false }));
                setFilteringInProgress(false);
            }
        }
    };

    const changeSearch = (searchString: string) => {
        setState((state) => ({
            ...state,
            searchString: searchString,
        }));
        setSearch(searchString);
    };

    const submitSearch = (e: any) => {
        setFilteringInProgress(true);
        setFilteredMode(true);
        if (!state.searchString) {
            setFilteredDivisions([]);
            setFilteringInProgress(false);
            return;
        }
        getData(filters, e.target.value);
    };

    const cleanFilter = () => {
        const value = '';
        setFilteringInProgress(true);
        changeSearch(value);
        if (!filters.ratingSystems?.length) {
            setFilteredMode(false);
            setFilteredDivisions([]);
            setFilteringInProgress(false);
        } else {
            setFilteringInProgress(true);
            setFilteredMode(true);
            getData(filters, value);
        }
    };

    const getProductIdsByPackageId = async (): Promise<boolean> => {
        if (projectInfo && packageId && packageId.length > 1) {
            const { payload } = (await dispatch(
                getProductIdsByPackageIdAction({
                    productPackageID: packageId,
                }),
            )) as GetProductIdsByPackageIdResponse;
            if (payload.result) {
                await addProductsToProject(
                    +projectInfo.id,
                    payload.result.map((r) => r.productSelectedOnTcId),
                );
                window.history.replaceState(null, '', `/project-info/${projectInfo?.id}/0/2`);
                history.push(`/project-info/${projectInfo?.id}/0/2`);
                return true;
            }
        }
        return false;
    };

    const addProductsToProject = async (projectId: number, productIds: Array<number>) => {
        const { payload } = (await dispatch(
            addProductsToProjectAction({ products: productIds, projectId: projectId }),
        )) as ActionResponse<AddProductsToProjectResponse>;
        if (payload.data.existingProducts.length) {
            existingProductIds.current = payload.data.existingProducts;
        }
    };

    const copyTokensFromCookiesIfAuthFailsAndGetProjectProducts = async (
        filtersToSet?: ProductFilters,
        searchString?: string,
    ) => {
        if (projectInfo) {
            const initial = (await dispatch(
                getProjectProductsWithCopyTokensOptionAction(projectInfo.id),
            )) as ActionResponse<Array<InternalProduct>>;

            if (initial?.payload?.data) {
                setProductIdsInfo(initial.payload.data);
                await getProductReferences();
                await getProductIds(initial.payload.data, filtersToSet, searchString);
            }
        }
    };

    const getProductIds = async (
        allProducts: Array<InternalProduct> | undefined,
        filtersToSet?: ProductFilters,
        searchString?: string,
    ) => {
        if (projectInfo) {
            await getProducts(allProducts, filtersToSet, searchString);
        }
    };

    const getProductReferences = async () => {
        const { payload } = (await dispatch(
            getProductReferencesAction(),
        )) as ActionResponse<ProductReferences>;
        setReferences(payload.data);
    };

    const getExternalProducts = async (ids: number[]) => {
        const { payload } = (await dispatch(
            getExternalProductsAction({ productIds: ids }),
        )) as ActionResponse<GetProductsFromTransparencyCatalogAPIResponse>;
        if (payload.data) {
            return flattenTcMfSectionProducts(payload.data.mfSection);
        }
        return null;
    };

    const fetchTagsInBatches = async (ids: number[]): Promise<ProjectProductTag[]> => {
        const allTags: ProjectProductTag[] = [];
        for (let i = 0; i < ids.length; i += TAGS_BATCH_SIZE) {
            const batch = ids.slice(i, i + TAGS_BATCH_SIZE);
            const result = await dispatch(
                getProjectProductTagsByProjectProductIdsAction(batch),
            ) as { payload: any };
            const batchTags: ProjectProductTag[] = result.payload ?? [];
            allTags.push(...batchTags);
        }
        return allTags;
    };

    const getProducts = async (
        internalProducts: Array<InternalProduct>,
        filtersToSet?: ProductFilters,
        searchString?: string,
    ) => {
        const requestId = ++productsLoadRequestId.current;
        const productIds = internalProducts.map((p) => p.productId);
        if (!productIds.length) {
            if (requestId !== productsLoadRequestId.current) {
                return;
            }
            setState((state) => ({
                ...state,
                products: [],
                pending: false,
            }));
            if (installedTab) {
                setInstalledDivisions([]);
            } else {
                setAllDivisions([]);
            }
            return;
        }
        const needsExternalRefresh =
            !productsExternal ||
            productsExternal.length === 0 ||
            internalProducts.length !== state.products.length ||
            productIds.some((pId) => !productsExternal.some((ep) => ep.id === pId));

        const availableProducts = needsExternalRefresh
            ? await getExternalProducts(productIds)
            : productsExternal;

        if (requestId !== productsLoadRequestId.current) {
            return;
        }

        const allProjectProductIds = internalProducts
            ?.map((p) => p.id)
            .filter((id): id is number => id != null)
            ?? [];

        const allTags = allProjectProductIds.length > 0
            ? await fetchTagsInBatches(allProjectProductIds)
            : [];

        if (requestId !== productsLoadRequestId.current) {
            return;
        }

        let freshTagsMap: Record<number, ProjectProductTag[]> | undefined;

        if (filters?.tags?.length) {
            freshTagsMap = {};
            for (const tag of allTags) {
                const pid = tag.projectProductId;
                if (!freshTagsMap[pid]) {
                    freshTagsMap[pid] = [];
                }
                freshTagsMap[pid].push(tag);
            }
        }

        const resolvedExternalProducts = availableProducts ?? [];
        setProductsExternalInfo(resolvedExternalProducts);
        setProductsExternal(resolvedExternalProducts);

        const currentProducts = mergeInternalProductsWithExternal(
            internalProducts,
            resolvedExternalProducts,
        ); // fallback row when TC has no match — keeps manual/OutsideTC visible (bug 892)

        const companies =
            needsExternalRefresh || !brandsInformation.length
                ? await getCompaniesOfProducts(currentProducts, dispatch)
                : brandsInformation;
        if (requestId !== productsLoadRequestId.current) {
            return;
        }
        setCompaniesExternalInfo(companies);
        setBrandsInformation(companies);

        const divisions = new Map();
        currentProducts.forEach((product) => {
            const key = parseInt(product.mfDivision.number[0] + product.mfDivision.number[1]);
            const highestSection = product.mfSectionLevel1 || product.mfSection;
            if (divisions.has(key)) {
                if (divisions.get(key).sections.has(highestSection.number.slice(0, 5))) {
                    divisions
                        .get(key)
                        .sections.get(highestSection.number.slice(0, 5))
                        .products.push(product);
                } else {
                    divisions.get(key).sections.set(highestSection.number.slice(0, 5), {
                        id: highestSection.number.slice(0, 5),
                        key: parseInt(highestSection.number[3] + highestSection.number[4]),
                        name: highestSection.label,
                        clicked: false,
                        products: new Array(product),
                    });
                }
            } else {
                divisions.set(key, {
                    id: product.mfDivision.number,
                    name: product.mfDivision.label,
                    key: key,
                    sections: new Map([
                        [
                            highestSection.number.slice(0, 5),
                            {
                                id: highestSection.number.slice(0, 5),
                                key: parseInt(
                                    highestSection.number[3] + highestSection.number[4],
                                ),
                                name: highestSection.label,
                                clicked: false,
                                products: new Array(product),
                            },
                        ],
                    ]),
                    clicked: false,
                });
            }
        });
        for (const division of divisions.values()) {
            division.sections = [...division.sections.values()].sort(
                (a: Section<Product>, b: Section<Product>) => a.key - b.key,
            );
        }
        const uniqueDivisions = [...divisions.values()].sort((a, b) => a.key - b.key);
        let uniqueInstalledDivisions: Array<Division<Product>>;
        if (installedTab) {
            uniqueInstalledDivisions = setInstalledDivisions(uniqueDivisions);
        } else {
            uniqueInstalledDivisions = setAllDivisions(uniqueDivisions);
        }
        if (existingProductIds.current && existingProductIds.current.length) {
            setExistingProducts({
                ...existingProducts,
                products: currentProducts
                    .filter((cp) =>
                        existingProductIds.current.some((id) => id === cp.product.productId),
                    )
                    .map((product) => ({ name: product.productName, id: product.id })),
                isModalOpen: true,
            });
        }
        const productId = queryParams.get('productId');
        if (productId) {
            const selectedProduct = resolvedExternalProducts.find(
                (product) => product.id.toString() === productId.toString(),
            );
            if (selectedProduct) {
                showSelectedProduct(selectedProduct, uniqueDivisions, uniqueInstalledDivisions);
            }
        }
        if (requestId !== productsLoadRequestId.current) {
            return;
        }
        setState((state) => ({
            ...state,
            products: currentProducts,
            pending: false,
        }));
        if (filtersToSet && Object.values(filtersToSet).some((filter) => Array.isArray(filter) ? filter.length > 0 : !!filter) || searchString) {
            const uniqueFilteredDivisions = !installedTab
                ? filterProducts(allDivisions, filtersToSet, searchString, freshTagsMap)
                : filterProducts(installedDivisions, filtersToSet, searchString, freshTagsMap);
            setFilteredDivisions([...uniqueFilteredDivisions.sort((a, b) => a.key - b.key)]);
            setFilteredMode(true);
        }
        setFilteringInProgress(false);
    };

    const doesProductMatchTags = (
        product: Product,
        filters: ProductFilters | undefined,
        freshTagsMap?: Record<number, ProjectProductTag[]>,
    ): boolean => {
        if (!filters?.tags?.length) {
            return true;
        }
        const ppId = product.projectProductId ?? product.product?.id ?? null;
        const projectProductTags: ProjectProductTag[] | undefined =
            freshTagsMap?.[ppId] ??
            (selector as any).projectProductTagReducer?.byProjectProductId?.[ppId]?.tags;
        if (!projectProductTags) {
            return false;
        }
        return filters.tags.some((t) =>
            projectProductTags.some((pt) => pt.tagId === t.id || pt.tag?.id === t.id),
        );
    };

    const filterProducts = (
        divs: Array<Division<Product>>,
        filtersToSet?: ProductFilters,
        searchString?: string,
        freshTagsMap?: Record<number, ProjectProductTag[]>,
    ) => {
        // If no filters and no search, return all divisions as-is
        const hasFilters = filtersToSet && Object.values(filtersToSet).some((filter) =>
            Array.isArray(filter) ? filter.length > 0 : !!filter
        );
        if (!hasFilters && !searchString) {
            return divs;
        }

        const filteredDs: Division<Product>[] = [];
        const strSearch = searchString ? searchString.toLowerCase() : null;

        console.log('strSearch:', strSearch);

        for (const div of divs.values()) {
            const filteredSections = [];
            for (const sect of div.sections.values()) {
                const filteredProducts = sect.products.filter((product) => {

                    const matchesSearch =
                        !strSearch ||
                        product.productName?.toLowerCase().includes(strSearch) ||
                        product.company?.toLowerCase().includes(strSearch) ||
                        matchesMasterFormatSearch(product, strSearch);

                    // FIX: changed every() to some()
                    const matchesRatingSystems =
                        !filtersToSet?.ratingSystems?.length ||
                        filtersToSet.ratingSystems.some((rs) =>{
                            const mappedTitle = RATING_SYSTEM_TITLE_MAP[rs.title] ?? rs.title;
                            return product.ratingSystems?.some((rating: any) =>
                                normalizeRating(rating).includes(normalizeRating(mappedTitle))
                        )
                    });

                    const matchesTags = doesProductMatchTags(product, filtersToSet, freshTagsMap);

                    const matchesEpds =
                        !filtersToSet?.epdFilters?.length ||
                        filtersToSet.epdFilters.some((epd) =>{
                            let result = product.epdType?.toLowerCase().includes(epd.title.replace('-', ' ').toLowerCase())
                            if(epd.title.toLowerCase().includes("optimized")){
                                result ||= product.isOptimizedEpd
                            }
                            return result;
                        }
                        );

                    const matchesSMEpds =
                        !filtersToSet?.smEpdsFilters?.length ||
                        filtersToSet.smEpdsFilters.some((smEpd) => {
                                let result = product.epdType?.toLowerCase().includes(smEpd.title.replace('-', ' ').toLowerCase()) &&
                                product.epdProgram?.toLowerCase().includes('sminds')
                                if(smEpd.title.toLowerCase().includes("optimized")){
                                    result ||= product.isOptimizedEpd
                                    && product.epdProgram?.toLowerCase().includes("sminds");
                                }
                                return result;
                            }
                        );

                    const matchesEC3 =
                        !filtersToSet?.ec3Filters?.length ||
                        filtersToSet.ec3Filters.some((ec3) =>
                            product.ec3EmbodiedCarbon?.toLowerCase().includes(ec3.title.toLowerCase())
                        );

                    const matchesRegulations =
                        !filtersToSet?.regulationsFilters?.length ||
                        filtersToSet.regulationsFilters.some((r) =>
                            product.regulations?.some((regulation) =>
                                regulation.toLowerCase().includes(r.title.toLowerCase())
                            )
                        );

                    const matchesMaterialsFilters =
                        !filtersToSet?.materialsFilters?.length ||
                        filtersToSet.materialsFilters.some((materialFilter) =>
                            handleMaterialsFilters(materialFilter, product)
                        );

                    const matchesLowEmmitingFilters =
                        !filtersToSet?.lowEmittingFilters?.length ||
                        filtersToSet.lowEmittingFilters.some((lem) =>
                            (product as any)[lem.id]
                        );

                    const matchesLemRegulations =
                        !filtersToSet?.lemRegulationsFilters?.length ||
                        filtersToSet.lemRegulationsFilters.some((r) =>
                            (product as any)[r.id]
                        );


                    return (
                        matchesSearch &&
                        matchesRatingSystems &&
                        matchesEpds &&
                        matchesSMEpds &&
                        matchesEC3 &&
                        matchesRegulations &&
                        matchesMaterialsFilters &&
                        matchesLowEmmitingFilters &&
                        matchesLemRegulations &&
                        matchesTags
                    );
                });

                if (filteredProducts && filteredProducts.length > 0) {
                    const filteredSection = { ...sect };
                    filteredSection.clicked = true;
                    filteredSection.products = filteredProducts;
                    filteredSections.push(filteredSection);
                }

            }

            if (filteredSections && filteredSections.length > 0) {
                const filteredD = { ...div };
                filteredD.clicked = true;
                filteredD.sections = filteredSections;
                filteredDs.push(filteredD);
            }

        }
        return filteredDs;
    };

    const handleMaterialsFilters = (materialFilter: ProductsFilterData, product: Product) => {
        switch (materialFilter.id) {
            case MaterialsFiltersIds.HPD:
                return Boolean(product.hpd);
            case MaterialsFiltersIds.HPDPW:
                return Boolean(product.perkinsWillPrecautionaryListFree);
            case MaterialsFiltersIds.HPDLBC:
                return Boolean(product.livingBuildingChallengeRedListFree);
            case MaterialsFiltersIds.HPDC2C:
                return Boolean(product.c2cRestrictSubstancesListFree);
            case MaterialsFiltersIds.Declare:
                return Boolean(product.declare);
            case MaterialsFiltersIds.DeclareLBCRedList:
            case MaterialsFiltersIds.DeclareLBCCopliant:
            case MaterialsFiltersIds.DeclareDeclared:
                return product.declareResults?.toLowerCase().includes(materialFilter.title.toLowerCase());
            case MaterialsFiltersIds.C2CCertified:
                return Boolean(product.c2cResults);
            case MaterialsFiltersIds.C2CCertifiedPlatinum:
            case MaterialsFiltersIds.C2CCertifiedGold:
            case MaterialsFiltersIds.C2CCertifiedSilver:
            case MaterialsFiltersIds.C2CCertifiedBronze:
                return product.c2cResults?.toLowerCase().includes(materialFilter.title.toLowerCase());
            case MaterialsFiltersIds.C2CMHC:
                return Boolean(product.c2cMHCResults);
            case MaterialsFiltersIds.C2CMHCPlatinum:
            case MaterialsFiltersIds.C2CMHCGold:
            case MaterialsFiltersIds.C2CMHCSilver:
            case MaterialsFiltersIds.C2CMHCBronze:
                return product.c2cMHCResults?.toLowerCase().includes(materialFilter.title.toLowerCase());
            case MaterialsFiltersIds.LPC:
                return product.lpc?.toLowerCase().includes(materialFilter.title.toLowerCase());
            case MaterialsFiltersIds.MFRI:
                return Boolean(product.mfrInv);
            default:
                return false;
        }
    }

    const showSelectedProduct = (
        product: ExternalProduct,
        divisions: Array<Division<Product>>,
        installedDivisions: Array<Division<Product>>,
    ) => {
        const currentDivisions = installedTab ? installedDivisions : divisions;
        const divisionIndex = currentDivisions.findIndex((d) => d.id === product.mfDivision.number);
        const highestSection = product.mfSectionLevel1 || product.mfSection;
        if (divisionIndex !== -1) {
            currentDivisions[divisionIndex].clicked = true;
            const sectionIndex = currentDivisions[divisionIndex].sections.findIndex(
                (s) => s.id === highestSection.number.slice(0, 5),
            );
            if (sectionIndex !== -1) {
                currentDivisions[divisionIndex].sections[sectionIndex].clicked = true;
                setTimeout(() => {
                    document.getElementById(`${product.id}`)?.scrollIntoView({
                        behavior: 'smooth',
                        block: 'center',
                        inline: 'nearest',
                    });
                }, 500);
            }
        }
    };

    const handleDivisionOpen = (id: string) => {
        const index = allDivisions.findIndex((division) => division.id === id);
        if (index !== -1) {
            const currentDivisions = allDivisions;
            currentDivisions[index].clicked = !currentDivisions[index].clicked;

            if (!currentDivisions[index].clicked) {
                setOpenedSectionsIds([]);
            }

            setDivisions(currentDivisions);
        }
    };

    const handleSectionOpen = (divisionId: string, sectionId: string) => {
        const divisionIndex = allDivisions.findIndex((division) => division.id === divisionId);
        const sectionIndex = allDivisions[divisionIndex].sections.findIndex(
            (section) => section.id === sectionId,
        );

        if (divisionIndex !== -1 && sectionIndex !== -1) {
            const currentDivisions = allDivisions;
            currentDivisions[divisionIndex].sections[sectionIndex].clicked =
                !currentDivisions[divisionIndex].sections[sectionIndex].clicked;

            updateOpenedSectionsIds(currentDivisions[divisionIndex].sections[sectionIndex].clicked, sectionId);
            setDivisions(currentDivisions);
        }
    };

    const handleInstalledDivisionOpen = (id: string) => {
        const index = installedDivisions.findIndex((division) => division.id === id);
        if (index !== -1) {
            const currentDivisions = installedDivisions;
            currentDivisions[index].clicked = !currentDivisions[index].clicked;

            if (!currentDivisions[index].clicked) {
                setOpenedSectionsIds([]);
            }

            setInstalledDivisions(currentDivisions);
        }
    };

    const handleFilteredDivisionOpen = (id: string) => {
        const index = filteredDivisions.findIndex((division) => division.id === id);
        if (index !== -1) {
            const currentDivisions = filteredDivisions;
            currentDivisions[index].clicked = !currentDivisions[index].clicked;

            if (!currentDivisions[index].clicked) {
                setOpenedSectionsIds([]);
            }

            setFilteredDivisions(currentDivisions);
        }
    };

    const handleInstalledSectionOpen = (divisionId: string, sectionId: string) => {
        const divisionIndex = installedDivisions.findIndex(
            (division) => division.id === divisionId,
        );
        const sectionIndex = installedDivisions[divisionIndex].sections.findIndex(
            (section) => section.id === sectionId,
        );

        if (divisionIndex !== -1 && sectionIndex !== -1) {
            const currentDivisions = installedDivisions;
            currentDivisions[divisionIndex].sections[sectionIndex].clicked =
                !currentDivisions[divisionIndex].sections[sectionIndex].clicked;

            updateOpenedSectionsIds(currentDivisions[divisionIndex].sections[sectionIndex].clicked, sectionId);
            setInstalledDivisions(currentDivisions);
        }
    };

    const handleFilteredSectionOpen = (divisionId: string, sectionId: string) => {
        const divisionIndex = filteredDivisions.findIndex((division) => division.id === divisionId);
        const sectionIndex = filteredDivisions[divisionIndex].sections.findIndex(
            (section) => section.id === sectionId,
        );
        if (divisionIndex !== -1 && sectionIndex !== -1) {
            const currentDivisions = filteredDivisions;
            currentDivisions[divisionIndex].sections[sectionIndex].clicked =
                !currentDivisions[divisionIndex].sections[sectionIndex].clicked;

            updateOpenedSectionsIds(currentDivisions[divisionIndex].sections[sectionIndex].clicked, sectionId);
            setFilteredDivisions(currentDivisions);
        }
    };

    const updateDivisions = (product: Product, isInstalled = false) => {
        const highestSection = product.mfSectionLevel1 || product.mfSection;
        const currentDivisions = allDivisions;
        const currentInstalledDivisions = installedDivisions;
        const divisionIndex = allDivisions.findIndex(
            (division) => division.id === product.mfDivision.number,
        );
        const sectionIndex = allDivisions[divisionIndex].sections.findIndex(
            (section) => section.id === highestSection.number.slice(0, 5),
        );

        const productIndex = allDivisions[divisionIndex].sections[sectionIndex].products.findIndex(
            (item) => item.id === product.id,
        );
        if (divisionIndex !== -1 && sectionIndex !== -1 && productIndex !== -1) {
            currentDivisions[divisionIndex].sections[sectionIndex].products[productIndex] = product;
        }
        setDivisions(currentDivisions);

        if (isInstalled) {
            handleInstalledProduct(currentInstalledDivisions, product);
            setInstalledDivisions(currentInstalledDivisions);
            return;
        }
        const installedDivisionIndex = installedDivisions.findIndex(
            (division) => division.id === product.mfDivision.number,
        );
        if (installedDivisionIndex !== -1) {
            const installedSectionIndex = installedDivisions[
                installedDivisionIndex
            ].sections.findIndex((section) => section.id === highestSection.number.slice(0, 5));
            if (installedSectionIndex !== -1) {
                const installedProductIndex = installedDivisions[installedDivisionIndex].sections[
                    installedSectionIndex
                ].products.findIndex((item) => item.id === product.id);
                if (installedProductIndex !== -1) {
                    currentInstalledDivisions[installedDivisionIndex].sections[
                        installedSectionIndex
                    ].products[installedProductIndex] = product;
                }
                setInstalledDivisions(currentInstalledDivisions);
            }
        }

        const currentFilteredDivisions = filteredDivisions;
        if (isFilterMode) {
            const filteredDivisionIndex = filteredDivisions.findIndex(
                (division) => division.id === product.mfDivision.number,
            );
            if (filteredDivisionIndex !== -1) {
                const filteredSectionIndex = filteredDivisions[
                    filteredDivisionIndex
                ].sections.findIndex((section) => section.id === highestSection.number.slice(0, 5));
                if (filteredSectionIndex !== -1) {
                    const filteredProductIndex = filteredDivisions[filteredDivisionIndex].sections[
                        filteredSectionIndex
                    ].products.findIndex((item) => item.id === product.id);
                    if (filteredProductIndex !== -1) {
                        currentFilteredDivisions[filteredDivisionIndex].sections[
                            filteredSectionIndex
                        ].products[filteredProductIndex] = product;
                    }
                    setFilteredDivisions(currentFilteredDivisions);
                }
            }
        }
    };

    const handleDeleteProduct = (product: Product) => {
        setAllDivisions(handleDeleteProductInSpecificDivisionList(allDivisions, product));
        if (isFilterMode) {
            setFilteredDivisions(
                handleDeleteProductInSpecificDivisionList(filteredDivisions, product),
            );
            if (installedTab) {
                setInstalledDivisions(
                    handleDeleteProductInSpecificDivisionList(installedDivisions, product),
                );
            }
        }
    };

    const handleDeleteMultipleProducts = (products: Product[]) => {
        const {
            updatedDivisions,
            updatedProducts
        } = handleDeleteMultipleProductsInSpecificDivisionList(allDivisions, products, state.products);

        setAllDivisions(updatedDivisions);

        setState((prevState) => ({
            ...prevState,
            products: updatedProducts,
        }));

        if (isFilterMode) {
            const {
                updatedDivisions: updatedFilteredDivisions,
                updatedProducts: updatedFilteredProducts
            } = handleDeleteMultipleProductsInSpecificDivisionList(
                filteredDivisions,
                products,
                state.products
            );
            setFilteredDivisions(updatedFilteredDivisions);

            setState((prevState) => ({
                ...prevState,
                products: updatedFilteredProducts,
            }));
        }

        if (installedTab) {
            const {
                updatedDivisions: updatedInstalledDivisions,
                updatedProducts: updatedInstalledProducts
            } = handleDeleteMultipleProductsInSpecificDivisionList(
                installedDivisions,
                products,
                state.products
            );
            setInstalledDivisions(updatedInstalledDivisions);

            setState((prevState) => ({
                ...prevState,
                products: updatedInstalledProducts,
            }));
        }
        setSelectedProducts([]);
    }

    const handleDeleteProductInSpecificDivisionList = (
        divisions: Array<Division<Product>>,
        product: Product,
    ) => {
        const highestSection = product.mfSectionLevel1 || product.mfSection;
        const divisionIndex = divisions.findIndex(
            (division) => division.id === product.mfDivision.number,
        );
        const sectionIndex = divisions[divisionIndex].sections.findIndex(
            (section) => section.id === highestSection.number.slice(0, 5),
        );

        const productIndex = divisions[divisionIndex].sections[sectionIndex].products.findIndex(
            (item) => item.id === product.id,
        );
        const currentDivisions = divisions;
        currentDivisions[divisionIndex].sections[sectionIndex].products.splice(productIndex, 1);
        if (!currentDivisions[divisionIndex].sections[sectionIndex].products.length) {
            currentDivisions[divisionIndex].sections.splice(sectionIndex, 1);
            if (!currentDivisions[divisionIndex].sections.length) {
                currentDivisions.splice(divisionIndex, 1);
            }
        }
        return currentDivisions;
    };

    const handleDeleteMultipleProductsInSpecificDivisionList = (
        divisions: Array<Division<Product>>,
        productsToDelete: Product[],
        allProducts: Product[],
    ) => {
        let updatedDivisions = [...divisions];

        productsToDelete.forEach((product) => {
            updatedDivisions = handleDeleteProductInSpecificDivisionList(updatedDivisions, product);
        });

        const updatedProducts = allProducts.filter(
            (product) => !productsToDelete.some((deleted) => deleted.id === product.id)
        );

        return {
            updatedDivisions,
            updatedProducts,
        };
    };


    const handleInstalledProduct = (
        installedDivisions: Array<Division<Product>>,
        product: Product,
    ) => {
        const highestSection = product.mfSectionLevel1 || product.mfSection;
        if (product.isInstalled) {
            const currentDivisionIndex = installedDivisions.findIndex(
                (division) => division.id === product.mfDivision.number,
            );
            if (currentDivisionIndex !== -1) {
                const currentSectionIndex = installedDivisions[
                    currentDivisionIndex
                ].sections.findIndex((section) => section.id === highestSection.number.slice(0, 5));
                if (currentSectionIndex !== -1) {
                    installedDivisions[currentDivisionIndex].sections[
                        currentSectionIndex
                    ].products.push(product);
                } else {
                    installedDivisions[currentDivisionIndex].sections.push({
                        id: highestSection.number.slice(0, 5),
                        key: parseInt(highestSection.number[3] + highestSection.number[4]),
                        name: highestSection.label,
                        clicked: false,
                        products: [product],
                    });
                }
            } else {
                installedDivisions.push({
                    id: product.mfDivision.number,
                    name: product.mfDivision.label,
                    key: parseInt(product.mfDivision.number[0] + product.mfDivision.number[1]),
                    clicked: false,
                    sections: [
                        {
                            id: highestSection.number.slice(0, 5),
                            key: parseInt(highestSection.number[3] + highestSection.number[4]),
                            name: highestSection.label,
                            clicked: false,
                            products: [product],
                        },
                    ],
                });
                installedDivisions = installedDivisions.sort((a, b) => a.key - b.key);
            }
        } else {
            const installedDivisionIndex = installedDivisions.findIndex(
                (division) => division.id === product.mfDivision.number,
            );
            const installedSectionIndex = installedDivisions[
                installedDivisionIndex
            ].sections.findIndex((section) => section.id === highestSection.number.slice(0, 5));

            const installedProductIndex = installedDivisions[installedDivisionIndex].sections[
                installedSectionIndex
            ].products.findIndex((item) => item.id === product.id);
            installedDivisions[installedDivisionIndex].sections[
                installedSectionIndex
            ].products.splice(installedProductIndex, 1);
            if (
                !installedDivisions[installedDivisionIndex].sections[installedSectionIndex].products
                    .length
            ) {
                installedDivisions[installedDivisionIndex].sections.splice(
                    installedSectionIndex,
                    1,
                );
                if (!installedDivisions[installedDivisionIndex].sections.length) {
                    installedDivisions.splice(installedDivisionIndex, 1);
                }
            }
        }
    };

    const closeWarnModal = () => {
        setExistingProducts({
            productIds: Array<number>(),
            products: Array<{ id: number; name: string }>(),
            isModalOpen: false,
        });
        existingProductIds.current = Array<number>();
        history.push(`/project-info/${projectInfo?.id}/0/2`);
    };

    const openCloseAll = async () => {
        let action;

        if (installedTab && !isFilterMode) {
            action = checkWhatActionNeeded(installedDivisions);
            openCloseAllProcess(installedDivisions, action);
        } else if (!installedTab && !isFilterMode) {
            action = checkWhatActionNeeded(allDivisions);
            openCloseAllProcess(allDivisions, action);
        } else if (isFilterMode) {
            action = checkWhatActionNeeded(filteredDivisions);
            openCloseAllProcess(filteredDivisions, action);
        }
        history.push(`/project-info/${projectInfo?.id}/0/2`);
    };

    const openCloseAllProcess = async (divisions: Array<Division<Product>>, action: boolean) => {
        if (!divisions || !divisions.length) {
            return;
        }

        const openedIds = [];

        for (const division of divisions) {
            division.clicked = action;
            if (!division.sections || !division.sections.length) {
                return;
            }
            for (const section of division.sections) {
                section.clicked = action;

                if (action) {
                    openedIds.push(section.id);
                }
            }
        }

        setOpenedSectionsIds(openedIds);
    };

    const checkWhatActionNeeded = (divisions: Array<Division<Product>>) => {
        for (const division of divisions) {
            if (!division.clicked) {
                return true;
            }
            for (const section of division.sections) {
                if (!section.clicked) {
                    return true;
                }
            }
        }
        return false;
    };

    const getBrandAndProductCount = (divisions: Array<Division<Product | BrandCompany>>) => {
        let productsCount = 0;
        const companiesNames = [];
        for (const division of divisions) {
            for (const element of division.sections) {
                const products = element.products
                productsCount += products.length;

                for (const product of products) {
                    if (product.company) {
                        companiesNames.push(product.company);
                    }
                }
            }
        }

        const uniqueCompanyNames = [...new Set(companiesNames)]

        return {
            products: productsCount,
            companies: uniqueCompanyNames.length
        };
    };

    const handleFilters = (filtersToSet: ProductFilters) => {
        setFilteringInProgress(true);
        setFilters(filtersToSet);

        const hasFilters = Object.values(filtersToSet).some((filter) =>
            Array.isArray(filter) ? filter.length > 0 : !!filter
        );
        const hasSearchString = !!state.searchString;

        if (!hasFilters && !hasSearchString) {
            setFilteredMode(false);
            setFilteredDivisions([]);
            setFilteringInProgress(false);
            return;
        }

        setFilteredMode(true);

        // FIX: filter locally against already-loaded divisions, no API refetch needed
        const sourceDivisions = installedTab ? installedDivisions : allDivisions;
        const uniqueFilteredDivisions = filterProducts(
            sourceDivisions,
            filtersToSet,
            state.searchString ?? undefined,
        );
        setFilteredDivisions([...uniqueFilteredDivisions.sort((a, b) => a.key - b.key)]);
        setFilteringInProgress(false);
    };

    const selectProduct = (event: CheckboxChangeEvent, product: Product) => {
        if (event.target.checked) {
            setSelectedProducts(prev => [...prev, product]);
        } else {
            setSelectedProducts(prev =>
                prev.filter(currentProduct => currentProduct.id !== product.id)
            );
        }
    };

    const selectAllProducts = (e: CheckboxChangeEvent) => {
        if (e.target.checked) {
            if (isFilterMode) {
                setSelectedProducts(getProductsInsideDivisions(filteredDivisions));
            }
            else if (installedTab) {
                setSelectedProducts(getProductsInsideDivisions(installedDivisions));
            }
            else {
                setSelectedProducts(state.products);
            }
        } else {
            setSelectedProducts([]);
        }
        setSelectAll(e.target.checked);
    };

    const getProductsInsideDivisions = (divisions: Array<Division<Product>>): Product[] => {
        return divisions.flatMap((division) =>
            division.sections.flatMap((section) => section.products)
        );
    };

    const updateOpenedSectionsIds = (clicked: boolean, sectionId: string) => {
        setOpenedSectionsIds(prevOpenedIds => {
            let openedIds = [...prevOpenedIds];
            if (clicked) {
                openedIds.push(sectionId);
            } else {
                openedIds = openedIds.filter(item => item !== sectionId);
            }
            return openedIds;
        });
    };

    // The division tree lives in the parent and is populated asynchronously, so
    // state.pending can flip to false while allDivisions/installedDivisions are
    // still empty during a load. Use the authoritative product counts from
    // projectInfo (refreshed on every add/copy/delete/import) to keep the loader
    // visible until the tree is actually built, so the "Add products" empty state
    // never flashes for a project that has products.
    const expectedTotalProducts = projectInfo?.totalProductCount ?? 0;
    const expectedInstalledProducts = projectInfo?.installedProductCount ?? 0;
    const awaitingAllProducts =
        !installedTab && !isFilterMode && expectedTotalProducts > 0 && allDivisions.length === 0;
    const awaitingInstalledProducts =
        installedTab && !isFilterMode && expectedInstalledProducts > 0 && installedDivisions.length === 0;
    const showLoader =
        state.pending || isFilteringInProgress || awaitingAllProducts || awaitingInstalledProducts;

    return (
        <ProjectProductTagsProvider>
            <ProductsTabPageContainer className={`${filterModalOpen ? "shifted" : ""}`}>
                <ProductsFilterModal
                    open={filterModalOpen}
                    onCancel={() => setFilterModalOpen(false)}
                    onFilter={handleFilters}
                />
                <ProductsTabHeader>
                    <ProductFilterContainer>
                        <ProductsSearch
                            searchString={searchText}
                            placeholder={MASTERFORMAT_SEARCH_PLACEHOLDER}
                            changeSearch={changeSearch}
                            cleanFilter={cleanFilter}
                            handleSearchSubmit={submitSearch}
                        />

                        <ProductsFilterButton onClick={() => filterModalOpen ? setFilterModalOpen(false) : setFilterModalOpen(true)} />
                    </ProductFilterContainer>

                    <SpaceEnd>
                        <ViewProjectInEC3Button>
                            {(projectInfo?.ec3ProjectStatus?.export?.status === EC3ExportStatus.Done ||
                                projectInfo?.id === ec3Reducer.ec3Project?.projectId) && (
                                    <EC3Link>
                                        <Image
                                            src="src/assets/images/arrow-up-right-from-square.svg"
                                            preview={false}
                                        />
                                        <a
                                            href={
                                                projectInfo?.ec3ProjectStatus?.eC3Project?.link ||
                                                ec3Reducer.ec3Project?.link
                                            }
                                            target="_blank"
                                        >
                                            <Text weight={400} size={14} height={18} color={blueLink}>
                                                View project in EC3 tool
                                            </Text>
                                        </a>
                                    </EC3Link>
                                )}
                        </ViewProjectInEC3Button>
                        <ProductsAction
                            projectId={projectInfo?.id}
                            projectRole={projectInfo?.userRole}
                            selectedProducts={selectedProducts}
                            copyProductsTo={copyProductsTo}
                            handleDeleteMultipleProducts={handleDeleteMultipleProducts}
                        />
                    </SpaceEnd>
                </ProductsTabHeader>

                {isFilterMode && !isFilteringInProgress && (
                    <FoundResultsCount weight={500} size={20} height={24} color={black}>
                        {!filteredDivisions.length || getBrandAndProductCount(filteredDivisions).products === 0
                            ? 'No results found'
                            : (() => {
                                const brandAndProductCount = getBrandAndProductCount(filteredDivisions);
                                const companySuffix = brandAndProductCount.companies > 1 ? 's' : '';
                                const productSuffix = brandAndProductCount.products > 1 ? 's' : '';

                                return `${brandAndProductCount.companies} brand${companySuffix} | ${brandAndProductCount.products} product${productSuffix} found`;
                            })()
                        }
                    </FoundResultsCount>
                )}

                {(!isFilterMode && allDivisions.length) ||
                    (isFilterMode && filteredDivisions.length) ? (
                    <>
                        <OpenCloseAllButton onClick={openCloseAll}>
                            <Text weight={400} size={12} height={14} color={blueLink}>
                                Open / close all
                            </Text>
                        </OpenCloseAllButton>
                        {openedSectionsIds.length ? (
                            <SelectAllContainer>
                                <SelectAllCheckbox
                                    onChange={(e) => selectAllProducts(e)}
                                    checked={selectAll}
                                />
                                <BoldText weight={400} size={12} height={14} color={blueLink}>
                                    Select all
                                </BoldText>
                            </SelectAllContainer>
                        ) : ''
                        }
                    </>
                ) : (
                    ''
                )}
                 {!showLoader ? (
                    <>
                        {installedTab && !isFilterMode && (
                            <ProductsTabContent>
                                {installedDivisions.length ? (
                                    <DivisionsContainer
                                        className={installedDivisions[0].clicked ? 'clicked' : ''}
                                    >
                                        {installedDivisions.map((division) => (
                                            <DivisionItem
                                                division={division}
                                                key={division.key}
                                                references={references}
                                                projectInfo={projectInfo}
                                                projectReferences={projectReferences}
                                                installedTab
                                                handleClicked={handleInstalledDivisionOpen}
                                                handleSectionOpen={handleInstalledSectionOpen}
                                                handleDeleteProduct={handleDeleteProduct}
                                                updateDivisions={updateDivisions}
                                                editable={getProjectPermission(
                                                    [
                                                        ProjectPermissionTypes.EDITOR,
                                                        ProjectPermissionTypes.OWNER,
                                                        ProjectPermissionTypes.PROJECT_ADMIN,
                                                    ],
                                                    projectInfo?.userRole,
                                                )}
                                                brandsInfo={brandsInformation}
                                                filterModalOpened={filterModalOpen}
                                                handleSelectProduct={selectProduct}
                                                selectedProducts={selectedProducts}
                                            />
                                        ))}
                                    </DivisionsContainer>
                                ) : (
                                    <NoProductsTitleContainer>
                                        <h1>
                                            In the Products tab, click the "Installed" icon next to
                                            products that were installed to create the list here.
                                        </h1>
                                    </NoProductsTitleContainer>
                                )}
                            </ProductsTabContent>
                        )}

                        {installedTab && isFilterMode && (
                            <ProductSpin size="large" spinning={ec3Reducer.pending}>
                                <ProductsTabContent>
                                    {filteredDivisions.length ? (
                                        <DivisionsContainer
                                            className={filteredDivisions[0].clicked ? 'clicked' : ''}
                                        >
                                            {filteredDivisions.map((division) => (
                                                <DivisionItem
                                                    division={division}
                                                    key={division.key}
                                                    references={references}
                                                    projectReferences={projectReferences}
                                                    projectInfo={projectInfo}
                                                    handleClicked={handleFilteredDivisionOpen}
                                                    handleSectionOpen={handleFilteredSectionOpen}
                                                    handleDeleteProduct={handleDeleteProduct}
                                                    updateDivisions={updateDivisions}
                                                    editable={getProjectPermission(
                                                        [
                                                            ProjectPermissionTypes.EDITOR,
                                                            ProjectPermissionTypes.OWNER,
                                                            ProjectPermissionTypes.PROJECT_ADMIN,
                                                        ],
                                                        projectInfo?.userRole,
                                                    )}
                                                    brandsInfo={brandsInformation}
                                                    filterModalOpened={filterModalOpen}
                                                    handleSelectProduct={selectProduct}
                                                    selectedProducts={selectedProducts}
                                                />
                                            ))}
                                        </DivisionsContainer>
                                    ) : (
                                        <NoProductsTitleContainerFiltered>
                                            <center>
                                                <Image
                                                    src="src/assets/images/empty-graphic 1.svg"
                                                    preview={false}
                                                    alt="no results found"
                                                />
                                                <h1>
                                                    <center>
                                                        Clear search field to
                                                        <br />
                                                        display products and try
                                                        <br />
                                                        your search again.
                                                    </center>
                                                </h1>
                                            </center>
                                        </NoProductsTitleContainerFiltered>
                                    )}
                                </ProductsTabContent>
                            </ProductSpin>
                        )}

                        {!installedTab && !isFilterMode && (
                            <ProductSpin size="large" spinning={ec3Reducer.pending}>
                                <ProductsTabContent>
                                    {allDivisions.length ? (
                                        <DivisionsContainer
                                            className={allDivisions[0].clicked ? 'clicked' : ''}
                                        >
                                            {allDivisions.map((division) => (
                                                <DivisionItem
                                                    division={division}
                                                    key={division.key}
                                                    references={references}
                                                    projectReferences={projectReferences}
                                                    projectInfo={projectInfo}
                                                    handleClicked={handleDivisionOpen}
                                                    handleSectionOpen={handleSectionOpen}
                                                    handleDeleteProduct={handleDeleteProduct}
                                                    updateDivisions={updateDivisions}
                                                    editable={getProjectPermission(
                                                        [
                                                            ProjectPermissionTypes.EDITOR,
                                                            ProjectPermissionTypes.OWNER,
                                                            ProjectPermissionTypes.PROJECT_ADMIN,
                                                        ],
                                                        projectInfo?.userRole,
                                                    )}
                                                    brandsInfo={brandsInformation}
                                                    filterModalOpened={filterModalOpen}
                                                    handleSelectProduct={selectProduct}
                                                    selectedProducts={selectedProducts}
                                                />
                                            ))}
                                        </DivisionsContainer>
                                    ) : (
                                        <NoProductsTitleContainer>
                                            <h1>Add products using the "Add & delete products" button above.</h1>
                                        </NoProductsTitleContainer>
                                    )}
                                </ProductsTabContent>
                            </ProductSpin>
                        )}

                        {!installedTab && isFilterMode && (
                            <ProductSpin size="large" spinning={ec3Reducer.pending}>
                                <ProductsTabContent>
                                    {filteredDivisions.length ? (
                                        <DivisionsContainer
                                            className={filteredDivisions[0].clicked ? 'clicked' : ''}
                                        >
                                            {filteredDivisions.map((division) => (
                                                <DivisionItem
                                                    division={division}
                                                    key={division.key}
                                                    references={references}
                                                    projectReferences={projectReferences}
                                                    projectInfo={projectInfo}
                                                    handleClicked={handleFilteredDivisionOpen}
                                                    handleSectionOpen={handleFilteredSectionOpen}
                                                    handleDeleteProduct={handleDeleteProduct}
                                                    updateDivisions={updateDivisions}
                                                    editable={getProjectPermission(
                                                        [
                                                            ProjectPermissionTypes.EDITOR,
                                                            ProjectPermissionTypes.OWNER,
                                                            ProjectPermissionTypes.PROJECT_ADMIN,
                                                        ],
                                                        projectInfo?.userRole,
                                                    )}
                                                    brandsInfo={brandsInformation}
                                                    filterModalOpened={filterModalOpen}
                                                    handleSelectProduct={selectProduct}
                                                    selectedProducts={selectedProducts}
                                                />
                                            ))}
                                        </DivisionsContainer>
                                    ) : (
                                        <NoProductsTitleContainerFiltered>
                                            <center>
                                                <Image
                                                    src="src/assets/images/empty-graphic 1.svg"
                                                    preview={false}
                                                    alt="no results found"
                                                />
                                                <h1>
                                                    <center>
                                                        Clear search field to
                                                        <br />
                                                        display products and try
                                                        <br />
                                                        your search again.
                                                    </center>
                                                </h1>
                                            </center>
                                        </NoProductsTitleContainerFiltered>
                                    )}
                                </ProductsTabContent>
                            </ProductSpin>
                        )}
                    </>
                ) : (
                    <ProductSpin size="large" spinning={showLoader}>
                        <ProductsTabContent></ProductsTabContent>
                    </ProductSpin>
                )}

                {existingProducts.isModalOpen && (
                    <ExistingProductsModal
                        isOpen={existingProducts.isModalOpen}
                        closeModal={closeWarnModal}
                        existingProducts={existingProducts.products}
                        addedFrom={'Transparency Catalog'}
                        addedTo={'project'}
                        currentPage={`/project-info/${projectInfo?.id}/0/2`}
                    />
                )}
            </ProductsTabPageContainer>
        </ProjectProductTagsProvider>
    );
};

export default ProductsTab;
